import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ChevronRight, UserPlus } from 'lucide-react';
import { AdaptiveModal } from '@/components/ui/AdaptiveModal';

type JoinFamilyDialogProps = {
  onJoinFamily: (inviteCode: string) => Promise<void>;
  isJoiningFamily: boolean;
  hasFamily: boolean;
  isSingleMemberFamily?: boolean;
  switchWarning?: string | null;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  customTrigger?: boolean;
};

export const JoinFamilyDialog: React.FC<JoinFamilyDialogProps> = ({
  onJoinFamily,
  isJoiningFamily,
  hasFamily,
  isSingleMemberFamily,
  switchWarning,
  triggerRef,
  customTrigger = false,
}) => {
  const [newInviteCode, setNewInviteCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleJoinFamily = async () => {
    if (!newInviteCode.trim()) return;
    
    setJoinError(null);
    try {
      await onJoinFamily(newInviteCode);
      setIsOpen(false);
    } catch (error: any) {
      setJoinError(error.message || 'Failed to join family. Please try again.');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setJoinError(null);
    setNewInviteCode('');
  };

  return (
    <>
      {customTrigger ? (
        <Button 
          className="hidden"
          data-join-family-trigger
          ref={triggerRef}
          onClick={() => setIsOpen(true)}
        >
          Join Family
        </Button>
      ) : (
        <Button size="sm" variant="outline" className="gap-1" onClick={() => setIsOpen(true)}>
          <UserPlus className="h-4 w-4" />
          Join Family
        </Button>
      )}

      <AdaptiveModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Join a Family"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Enter the invite code you received to join a family.
            </p>
            {hasFamily && !isSingleMemberFamily && (
              <Alert className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {switchWarning || "This will remove you from your current family."}
                </AlertDescription>
              </Alert>
            )}
            <Input
              id="inviteCode"
              placeholder="Enter invite code"
              value={newInviteCode}
              onChange={(e) => setNewInviteCode(e.target.value)}
            />
          </div>
          {joinError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{joinError}</AlertDescription>
            </Alert>
          )}
        </div>
        
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleJoinFamily}
            disabled={isJoiningFamily || !newInviteCode.trim()}
          >
            {isJoiningFamily ? 'Joining...' : 'Join Family'}
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </AdaptiveModal>
    </>
  );
};
